import React, { useState } from 'react';
import './LanguageSelection.scss';

interface LanguageOption {
  label: string;
  value: string;
}

interface LanguagePopupProps {
  isOpen: boolean;
  onClose: () => void;
  languageOptions: LanguageOption[];
  onLanguageSelect: (language: string) => void;
  primaryColor?: string;
  currentLanguage?: string;
  languageSelectionConfig?: any;
}

const LanguagePopup: React.FC<LanguagePopupProps> = ({
  isOpen,
  onClose,
  languageOptions,
  onLanguageSelect,
  primaryColor = '#C3231E',
  currentLanguage = 'en',
  languageSelectionConfig = {}
}) => {
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');

  // Helper function to get translated text with backward compatibility
  const getTranslatedText = (textObj: any, fallback = '') => {
    // If it's a string (old config format), return as-is
    if (typeof textObj === 'string') return textObj;
    
    // If it's an object (new multilingual format)
    if (typeof textObj === 'object' && textObj !== null) {
      return textObj[currentLanguage] || textObj['en'] || fallback;
    }
    
    // If no value provided, return fallback
    return fallback;
  };

  const chooseLanguageText = getTranslatedText(languageSelectionConfig?.popupLabels?.chooseLanguage, 'Choose Language');
  const continueText = getTranslatedText(languageSelectionConfig?.popupLabels?.continue, 'Continue');

  if (!isOpen) return null;

  const handleLanguageChange = (value: string) => {
    setSelectedLanguage(value);
  };

  const handleContinue = () => {
    if (selectedLanguage) {
      onLanguageSelect(selectedLanguage);
      onClose();
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="language-popup-overlay" onClick={handleOverlayClick}>
      <div className="language-popup">
        <button 
          className="language-popup-close" 
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
        
        <h3 className="language-popup-title">{chooseLanguageText}</h3>
        
        <div className="language-options">
          {languageOptions.map((option) => (
            <label 
              key={option.value} 
              className="language-option"
            >
              <input
                type="radio"
                name="language"
                value={option.value}
                checked={selectedLanguage === option.value}
                onChange={() => handleLanguageChange(option.value)}
                className="language-radio"
              />
              <span className="language-label">{option.label}</span>
            </label>
          ))}
        </div>
        
        <button 
          className="language-continue-button"
          onClick={handleContinue}
          disabled={!selectedLanguage}
          style={{ backgroundColor: primaryColor }}
        >
          {continueText}
        </button>
      </div>
    </div>
  );
};

export default LanguagePopup;